import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, BookOpen, Sparkles, ArrowRight, Heart, Lightbulb, Clock, Users, Home, Activity, MessageCircle } from "lucide-react";

/**
 * Página "Preguntas frecuentes"
 * Material clínico basado en el documento "VISION TOTAL · Preguntas Frecuentes"
 * de Salud y Visión. Estructurado por categorías con desplegables.
 */

const FAQ_GROUPS = [
  {
    id: "general",
    icon: Lightbulb,
    title: "Lo esencial",
    intro: "Empieza por aquí si quieres entender qué hacemos y para quién.",
    items: [
      {
        q: "¿Esto es solo para niños con problemas de visión?",
        a: "No. También está indicado para niños, adolescentes e incluso adultos que, aunque ven nítido, tienen dificultades para procesar lo que ven. Muchos pacientes no tienen problemas de agudeza visual, pero sí de coordinación ocular, enfoque, atención visual, lectura, escritura o comprensión. También es muy eficaz en casos de ojo vago (ambliopía), estrabismo o dificultades de aprendizaje no explicadas por otras causas.",
      },
      {
        q: "¿En qué se diferencia de usar gafas compensatorias?",
        a: "Las gafas ayudan a ver nítido, pero no enseñan al cerebro a interpretar correctamente la información visual. La terapia visual trabaja las habilidades visuales funcionales: la coordinación de ambos ojos, la movilidad ocular, la atención visual y la percepción. Es un entrenamiento para que el sistema visual funcione de forma más eficaz, especialmente al leer, escribir o en actividades cotidianas.",
      },
      {
        q: "¿Funciona en adolescentes y adultos?",
        a: "Sí. Aunque el sistema nervioso es más plástico en la infancia, en la adolescencia y adultez también se pueden conseguir grandes cambios. Adaptamos el tratamiento al nivel madurativo, los objetivos personales y la disponibilidad del paciente. Trabajamos tanto el rendimiento académico/profesional como la visión funcional y la calidad de vida.",
      },
      {
        q: "¿Cómo sé si mi hijo/a (o yo mismo) lo necesita?",
        a: "Realizamos una evaluación optométrica comportamental muy completa. No solo miramos la agudeza visual: estudiamos cómo enfoca, cómo mueve los ojos, cómo coordina ambos ojos, cómo interpreta lo que ve y cómo todo eso impacta en el aprendizaje y el comportamiento. De ahí sale un informe detallado con conclusiones y propuesta de tratamiento personalizado.",
      },
    ],
  },
  {
    id: "tratamiento",
    icon: Activity,
    title: "Sobre el tratamiento",
    intro: "Cómo es la terapia visual y qué esperar del proceso.",
    items: [
      {
        q: "¿Es invasivo?",
        a: "No. Es un tratamiento totalmente natural, respetuoso y no invasivo. No implica medicamentos ni procedimientos dolorosos. Trabajamos con luz, estímulos visuales y ejercicios lúdicos adaptados a cada edad. Es seguro y bien tolerado incluso por niños pequeños o personas sensibles.",
      },
      {
        q: "¿Se cura el problema?",
        a: "La terapia visual no \"cura\" en el sentido médico, pero sí permite reeducar y optimizar la manera en que el cerebro utiliza la visión. El objetivo es que el paciente adquiera nuevas habilidades y automatismos visuales que le permitan ver, entender y reaccionar mejor a su entorno. Es un trabajo de neuroplasticidad visual y funcional.",
      },
      {
        q: "¿Cuándo se ven resultados?",
        a: "En muchos casos, las familias notan cambios en pocas semanas — especialmente en la atención, la disposición al estudio y el cansancio visual. Otros avances, como la mejora en la lectura o el comportamiento, se observan progresivamente. El ritmo depende de cada caso, pero siempre hacemos un seguimiento individualizado y transparente.",
      },
      {
        q: "¿Por qué es largo el tratamiento?",
        a: "Porque buscamos una mejora real y duradera. Reeducar el sistema visual implica crear nuevas conexiones neuronales y reforzarlas con práctica. Igual que un atleta necesita meses para entrenar su cuerpo, los pacientes necesitan tiempo para integrar habilidades visuales. La duración también depende del grado de dificultad y del compromiso con los ejercicios en casa.",
      },
      {
        q: "¿Se puede hacer solo una parte del plan?",
        a: "Recomendamos seguir el plan completo porque está diseñado para lograr una evolución progresiva y global. Interrumpirlo antes de tiempo puede dejar aprendizajes incompletos y limitar los beneficios. Nuestros packs incluyen todas las fases necesarias: evaluación, trabajo activo, seguimiento y refuerzo.",
      },
      {
        q: "¿Es compatible con otras terapias?",
        a: "Sí, totalmente. De hecho, muchos pacientes también están en tratamiento con logopedia, psicología o neuropsicología. La terapia visual potencia la eficacia de otras intervenciones al mejorar la base sensorial, la atención, la coordinación y la lectura. Trabajamos en equipo con otros profesionales siempre que es necesario.",
      },
      {
        q: "¿Y si no mejora?",
        a: "Desde el inicio planteamos objetivos medibles y hacemos seguimiento continuo. Si en algún momento detectamos que el progreso no es el esperado, revisamos el plan, lo ajustamos o lo complementamos con otras herramientas (como Syntonic o Tomatis). Buscamos siempre la mayor efectividad adaptada a la evolución real del paciente.",
      },
    ],
  },
  {
    id: "sesiones",
    icon: Clock,
    title: "Sesiones y duración",
    intro: "Frecuencia, número de sesiones y flexibilidad.",
    items: [
      {
        q: "¿El número de sesiones que nos dan al inicio es fijo?",
        a: "No necesariamente. La estimación inicial se basa en la evaluación y en nuestra experiencia con casos similares. Cada persona es única y su evolución también. A veces se necesita más tiempo para consolidar cambios, especialmente si hay múltiples áreas afectadas o si el sistema nervioso necesita más repetición para integrar los aprendizajes.",
      },
      {
        q: "¿Qué puede hacer que el tratamiento se alargue?",
        a: "Principalmente dos factores: (1) la respuesta individual del paciente — algunos necesitan más repeticiones o ajustes para alcanzar los objetivos — y (2) el compromiso con los ejercicios en casa. Cuando no se sigue el plan o no hay continuidad en casa, la evolución es más lenta.",
      },
      {
        q: "¿Que dure más significa que el tratamiento no está funcionando?",
        a: "No. Que se necesiten más sesiones no significa que algo esté fallando. Cada sistema nervioso tiene su ritmo. Lo importante es no forzar ni acortar pasos que el paciente aún necesita. Evaluamos continuamente y adaptamos el tratamiento para que sea efectivo, aunque eso implique alargar el proceso.",
      },
      {
        q: "¿Puedo saber de antemano cuántas sesiones necesitará mi hijo/a?",
        a: "Hacemos todo lo posible por ofrecer una estimación realista desde el inicio, pero no podemos garantizar un número exacto. Lo que sí hacemos es un seguimiento constante y transparente. Si vemos que necesita más tiempo, lo explicamos con claridad basándonos en los avances reales.",
      },
      {
        q: "¿Y si falto a una sesión?",
        a: "Nuestro tratamiento es flexible. Si por alguna razón no puedes asistir, simplemente reprogramamos la sesión — sin penalización. Lo importante es mantener la constancia dentro de un ritmo razonable para que la terapia tenga buen efecto acumulativo.",
      },
      {
        q: "¿Y si no podemos hacer más sesiones de las pactadas al inicio?",
        a: "Entendemos que cada familia tiene sus tiempos y posibilidades. Si en algún momento no podéis continuar como estaba previsto, lo hablaremos para encontrar la mejor alternativa: adaptar los objetivos, espaciar las sesiones o reforzar el trabajo en casa. Lo importante es no abandonar el proceso sin valorar opciones.",
      },
    ],
  },
  {
    id: "casa",
    icon: Home,
    title: "Ejercicios en casa",
    intro: "El trabajo en casa es fundamental — aquí resolvemos las dudas más típicas.",
    items: [
      {
        q: "¿Qué pasa si no hacen los ejercicios en casa?",
        a: "Los ejercicios en casa son clave para consolidar lo trabajado en consulta. Sin esa práctica, los avances pueden ser más lentos o no llegar a consolidarse. Igual que en el colegio no basta con ir a clase sin estudiar en casa, el refuerzo domiciliario permite que el cerebro integre y automatice las nuevas habilidades. Damos pautas claras, adaptadas al tiempo y la edad de cada paciente.",
      },
      {
        q: "¿Qué consecuencias hay si no se sigue el plan marcado?",
        a: "La constancia es fundamental. El tratamiento es un entrenamiento progresivo. Saltarse sesiones, no hacer ejercicios o no respetar los tiempos recomendados puede provocar estancamientos o retrocesos. Ajustamos el plan a cada familia, pero es necesaria la implicación. Nuestro objetivo no es exigir, sino acompañarte para que puedas sostener el proceso de forma realista y efectiva.",
      },
      {
        q: "¿Se pierde el tratamiento si no se hace bien en casa?",
        a: "No se pierde, pero se ralentiza. Siempre hay margen de ajuste y podemos retomar o reforzar lo que haga falta. Pero seamos honestos: la terapia no es mágica, es un proceso de trabajo conjunto. Cuanto más alineados estemos, mejores y más rápidos serán los resultados.",
      },
      {
        q: "¿Y si mi hijo/a no quiere hacer los ejercicios en casa?",
        a: "Es normal que a veces se muestren reacios, como con los deberes. Por eso adaptamos los ejercicios al estilo de cada niño y damos estrategias para motivarlos. Si aún así cuesta, lo hablamos en sesión. Nuestro equipo está preparado para ayudarte a convertir ese reto en una oportunidad.",
      },
      {
        q: "¿Puedo decirle que solo lo haga si le apetece?",
        a: "Recomendamos establecer los ejercicios como una parte más de la rutina diaria, igual que lavarse los dientes. Si lo dejamos a su voluntad, es probable que no se genere el hábito. Lo importante no es que lo hagan perfecto, sino que lo hagan con regularidad.",
      },
    ],
  },
  {
    id: "experiencia",
    icon: Users,
    title: "La experiencia del paciente",
    intro: "Cómo vive el niño (o adulto) la terapia, qué pasa en sesión y cómo es la comunicación con la familia.",
    items: [
      {
        q: "¿Y si mi hijo/a no quiere venir?",
        a: "Las sesiones están diseñadas para ser dinámicas y motivadoras. Usamos juegos, tecnología como WIVI y ejercicios con materiales divertidos. La mayoría de los niños vienen encantados. Desde el primer momento perciben que se sienten más seguros y capaces, lo que refuerza su implicación.",
      },
      {
        q: "¿Puedo ver lo que hacen en la sesión?",
        a: "Durante las sesiones, por respeto a la privacidad del resto de pacientes en sala, los padres no pueden estar presentes. Sin embargo, grabamos parte de los ejercicios para que puedan practicarse en casa y damos feedback individual después de cada sesión o revisión. El contacto con la familia es directo y constante.",
      },
    ],
  },
];

// ==== Item desplegable ====
const FAQItem = ({ q, a, idx, groupId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl transition-all"
      style={{
        background: open ? "var(--sage-50)" : "var(--cream)",
        border: `1px solid ${open ? "var(--sage-200)" : "var(--sage-100)"}`,
      }}
      data-testid={`faq-item-${groupId}-${idx}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-5 flex items-center justify-between gap-4"
        aria-expanded={open}
      >
        <span className="font-display text-base md:text-lg" style={{ color: "var(--ink)" }}>{q}</span>
        <span
          className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-transform"
          style={{
            background: open ? "var(--sage-500)" : "var(--sage-100)",
            color: open ? "var(--cream)" : "var(--sage-600)",
            transform: open ? "rotate(45deg)" : "rotate(0deg)",
          }}
        >
          <Plus size={16} />
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 font-sans-body text-sm md:text-[15px] leading-relaxed" style={{ color: "var(--ink-soft)" }}>
          {a}
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  // Filtrar items en función de búsqueda
  const filteredGroups = FAQ_GROUPS.map((g) => {
    if (!q) return g;
    const items = g.items.filter(
      (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
    );
    return items.length ? { ...g, items } : null;
  }).filter(Boolean);

  const totalMatches = filteredGroups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <main data-testid="faq-page">
      {/* HERO */}
      <section className="py-20 px-6" style={{ background: "var(--bg-soft)" }}>
        <div className="max-w-4xl mx-auto fade-up">
          <span className="section-eyebrow">Resolvemos tus dudas</span>
          <h1 className="section-title mt-4">
            Preguntas <span className="handwritten">frecuentes</span>
          </h1>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-6 max-w-2xl text-lg leading-relaxed">
            Te contamos con calma lo que más nos preguntan sobre la terapia visual y nuestro tratamiento — desde si es invasivo, cuánto dura o por qué los ejercicios en casa importan. <strong>Si no encuentras la respuesta, escríbenos sin compromiso.</strong>
          </p>
          <div className="mt-7 inline-flex items-center gap-2 px-4 py-2 rounded-full font-sans-body text-xs" style={{ background: "var(--sage-100)", color: "var(--sage-700)" }}>
            <BookOpen size={14} /> Basado en la guía clínica de Salud y Visión
          </div>

          {/* Buscador */}
          <div className="mt-10 relative max-w-xl">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "var(--sage-500)" }} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Busca una pregunta — por ejemplo: 'casa', 'adultos', 'sesiones'..."
              className="w-full pl-12 pr-4 py-3 rounded-full font-sans-body text-sm"
              style={{ border: "1.5px solid var(--sage-100)", background: "var(--cream)" }}
              data-testid="faq-search"
            />
            {q && (
              <div className="mt-3 font-sans-body text-sm" style={{ color: "var(--sage-600)" }}>
                {totalMatches} resultado{totalMatches !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* QUICK NAV */}
      {!q && (
        <section className="px-6 -mt-8">
          <div className="max-w-5xl mx-auto flex flex-wrap gap-2 justify-center">
            {FAQ_GROUPS.map((g) => (
              <a key={g.id} href={`#${g.id}`} className="px-4 py-2 rounded-full font-sans-body text-sm transition-colors" style={{ background: "var(--cream)", color: "var(--sage-600)", border: "1px solid var(--sage-100)" }} data-testid={`faq-quicklink-${g.id}`}>
                {g.title}
              </a>
            ))}
          </div>
        </section>
      )}

      {/* GROUPS */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-16">
          {filteredGroups.length === 0 && (
            <div className="text-center font-sans-body" style={{ color: "var(--ink-soft)" }}>
              No encontramos respuestas para "{query}". <Link to="/#contacto" className="underline" style={{ color: "var(--sage-600)" }}>Escríbenos</Link> y te respondemos personalmente.
            </div>
          )}
          {filteredGroups.map((g, idx) => (
            <article key={g.id} id={g.id} className="scroll-mt-24" data-testid={`faq-group-${g.id}`}>
              <div className="flex items-center gap-4 mb-3">
                <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: "var(--sage-500)" }}>
                  <g.icon size={22} style={{ color: "var(--cream)" }} />
                </div>
                <div>
                  <div className="section-eyebrow">{String(idx + 1).padStart(2, "0")}</div>
                  <h2 className="text-2xl md:text-3xl font-medium" style={{ color: "var(--ink)" }}>{g.title}</h2>
                </div>
              </div>
              <p className="font-sans-body text-[color:var(--ink-soft)] mb-6 max-w-2xl">{g.intro}</p>
              <div className="space-y-3">
                {g.items.map((it, i) => (
                  <FAQItem key={i} idx={i} groupId={g.id} q={it.q} a={it.a} />
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 px-6" style={{ background: "var(--sage-50)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <Heart size={28} className="mx-auto" style={{ color: "var(--sage-500)" }} />
          <h2 className="section-title mt-4" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}>
            ¿No has encontrado tu <span className="handwritten">respuesta</span>?
          </h2>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-5 max-w-2xl mx-auto leading-relaxed">
            Cada caso es distinto. Si tienes una duda más concreta o quieres saber si nuestro tratamiento es para ti, escríbenos sin compromiso — te respondemos en 24-48h.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/#contacto" className="btn-primary" data-testid="faq-cta-contact">
              <MessageCircle size={16} /> Pregúntanos por privado
            </Link>
            <Link to="/sintomas" className="btn-ghost" data-testid="faq-cta-symptoms">
              ¿Tienes síntomas? <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-8 inline-flex items-center gap-2 font-sans-body text-xs" style={{ color: "var(--sage-600)" }}>
            <Sparkles size={12} /> Te leemos siempre. Sin compromiso.
          </div>
        </div>
      </section>
    </main>
  );
};

export default FAQPage;
